import React from "react";
import { FormControl, MenuItem, Divider, TextField } from "@mui/material";
import { Temporal } from "@js-temporal/polyfill";
import { Autocomplete, CircularProgress } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import EditUserModal from "../../administration/UserData/EditUserModal";
import UserRolesModel from "../../../models/UserRoles";
import UserGroups from "../../../models/UserGroups";
import Users from "../../../models/Users";

interface OptionType {
  personalNr: string;
  name: string;
}

const ResponsiblePerson = (props: {
  user: Users;
  userRoles: UserRolesModel[];
  userGroups: UserGroups[];
  fetch: (input: string) => Promise<OptionType[]>;
  handleChangePerson: (event: any) => void;
  handleShowEditUserModal: () => void;
  handleHideEditUserModal: () => void;
  handleEditChange: (event: any) => void;
  editUser: (event: any) => void;
  showEditUserModal: boolean;
  userToEdit: Users;
}) => {
  const [options, setOptions] = React.useState<OptionType[]>([]);
  const [inputValue, setInputValue] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const userRoles: JSX.Element[] = (props.userRoles as UserRolesModel[]).map(
    (x) => {
      return (
        <MenuItem key={x.id} value={x.id}>
          {x.description}
        </MenuItem>
      );
    }
  );

  const userGroups: JSX.Element[] = (props.userGroups as UserGroups[]).map(
    (x) => {
      return (
        <MenuItem key={x.id} value={x.id}>
          {x.groupName}
        </MenuItem>
      );
    }
  );

  const selectedValue = props.user.personalNr
    ? {
        personalNr: props.user.personalNr,
        name: `${props.user.firstname} ${props.user.lastname}`,
      }
    : null;
  React.useEffect(() => {
    let active = true;

    if (inputValue === "") {
      setOptions([]);
      return;
    }

    setLoading(true);
    props.fetch(inputValue).then((results) => {
      if (active) {
        setOptions(results);
        setLoading(false);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, props.fetch]);

  return (
    <div className="card">
      <div className="card-header">Verantwortlicher</div>
      <div className="card-body">
        <div className="row">
          <div className="col-sm-4">
            <FormControl variant="outlined"  style={{ width: props.user.id !== undefined ? '90%' : '100%' }}>
              <Autocomplete
                value={selectedValue}
                onChange={(event, newValue) => {
                  if (newValue) {
                    props.handleChangePerson({
                      target: {
                        name: "",
                        value: newValue.personalNr,
                        type: "async",
                      },
                    });
                  } else {
                    props.handleChangePerson({
                      target: {
                        name: "",
                        value: "",
                        type: "async",
                      },
                    });
                  }
                }}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                getOptionLabel={(option) =>
                  `${option.personalNr} - ${option.name}`
                }
                isOptionEqualToValue={(option, value) =>
                  option.personalNr === value.personalNr
                }
                options={options}
                loading={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="PersonalNr"
                    placeholder="PersonalNr"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>
            {props.user.id !== undefined && (
              <EditIcon
                style={{ float: "right"}}
                className="icon"
                titleAccess="Verantwortlichen bearbeiten"
                onClick={() => props.handleShowEditUserModal()}
              />
            )}
          </div>
        </div>
        <Divider className="mt-3" variant="middle" />
        <div className="row">
          <div className="col-sm-4">
            <TextField
              label="Verantwortlicher"
              value={`${
                typeof props.user.firstname !== "undefined"
                  ? props.user.firstname
                  : ""
              } ${
                typeof props.user.lastname !== "undefined"
                  ? props.user.lastname
                  : ""
              }`}
              disabled
              fullWidth
            />
          </div>
        </div>
        <Divider className="mt-3" variant="middle" />
        <div className="row">
          <div className="col-sm-4">
            <TextField
              label="Personalnummer"
              value={
                typeof props.user.personalNr !== "undefined"
                  ? props.user.personalNr
                  : ""
              }
              disabled
              fullWidth
            />
          </div>
          <div className="col-sm-4 offset-sm-2">
            <TextField
              label="Geburtsdatum"
              value={
                props.user.birthdate
                  ? Temporal.PlainDate.from(
                      new Date(props.user.birthdate).toISOString().split("T")[0]
                    ).toString()
                  : ""
              }
              disabled
              fullWidth
            />
          </div>
        </div>
        <Divider className="mt-3" variant="middle" />
        <div className="row">
          <div
            className={
              props.user.email != null
                ? "col-sm-4"
                : props.user.id !== undefined
                ? "col-sm-4 warning"
                : "col-sm-4"
            }
          >
            <TextField
              label="Email"
              value={props.user.email != null ? props.user.email : ""}
              disabled
              fullWidth
            />
          </div>
          <div
            className={
              props.user.phone != null
                ? "col-sm-4 offset-sm-2"
                : props.user.id !== undefined
                ? "col-sm-4 offset-sm-2 warning"
                : "col-sm-4 offset-sm-2"
            }
          >
            <TextField
              label="Telefon"
              value={props.user.phone != null ? props.user.phone : ""}
              disabled
              fullWidth
            />
          </div>
        </div>
        <Divider className="mt-3" variant="middle" />
        <div className="row">
          <div className="col-sm-4">
            <TextField
              label="Registriert"
              value={
                props.user.registered != null
                  ? props.user.registered === true
                    ? "ja"
                    : "nein"
                  : ""
              }
              disabled
              fullWidth
            />
          </div>
          <div
            className={
              props.user.acceptedTerms != null
                ? props.user.acceptedTerms === true
                  ? "col-sm-4 offset-sm-2"
                  : "col-sm-4 offset-sm-2 warning"
                : props.user.id !== undefined
                ? "col-sm-4 offset-sm-2 warning"
                : "col-sm-4 offset-sm-2"
            }
          >
            <TextField
              label="Datenschutz"
              value={
                props.user.acceptedTerms != null
                  ? props.user.acceptedTerms === true
                    ? "ja"
                    : "nein"
                  : ""
              }
              disabled
              fullWidth
            />
          </div>
        </div>
        <Divider className="mt-3" variant="middle" />
        <div className="row">
          <div className="col-sm-4">
            <TextField
              label="Intern"
              value={
                props.user.internalEmployee != null
                  ? props.user.internalEmployee === true
                    ? "ja"
                    : "nein"
                  : ""
              }
              disabled
              fullWidth
            />
          </div>
          <div className="col-sm-4 offset-sm-2">
            <TextField
              label="Aktiv"
              value={
                props.user.active != null
                  ? props.user.active === true
                    ? "ja"
                    : "nein"
                  : ""
              }
              disabled
              fullWidth
            />
          </div>
        </div>
        <Divider className="mt-3" variant="middle" />
        <div className="row">
          <div className="col-sm-4">
            <TextField
              label="Benutzergruppe"
              value={
                props.user.userGroupName != null ? props.user.userGroupName : ""
              }
              disabled
              fullWidth
            />
          </div>
          <div className="col-sm-4 offset-sm-2">
            <TextField
              label="Vereinsnummer"
              value={props.user.clubNumber != null ? props.user.clubNumber : ""}
              disabled
              fullWidth
            />
          </div>
        </div>
        {props.showEditUserModal && (
          <EditUserModal
            user={props.userToEdit}
            hide={props.handleHideEditUserModal}
            submit={props.editUser}
            changeNewObject={props.handleEditChange}
            userRoles={userRoles}
            userGroups={userGroups}
          />
        )}
      </div>
    </div>
  );
};

export default ResponsiblePerson;
